import { Play } from '@phosphor-icons/react';
import { FORCE_MULTIPLIER_COPY, SHOWCASE_ITEMS } from './landingContent';
import { useLandingReveal, usePrefersReducedMotion } from './useLandingReveal';

/**
 * Connector curves in the canvas's 0-100 space. They end on the left edge
 * of the group column (x = 42, since the column is 58% wide and flush
 * right) and on the vertical centre of each card. The group column is a
 * grid of three equal rows with the cards centred, so those centres are
 * always 1/6, 3/6 and 5/6 of the height no matter how tall a card grows.
 */
const GROUPS_LEFT_EDGE = 42;
const CONNECTOR_PATHS = [
  `M 0 50 C 16 50, 24 16.667, ${GROUPS_LEFT_EDGE} 16.667`,
  `M 0 50 C 16 50, 24 50, ${GROUPS_LEFT_EDGE} 50`,
  `M 0 50 C 16 50, 24 83.333, ${GROUPS_LEFT_EDGE} 83.333`,
];

const SOURCE_EXAMPLE =
  SHOWCASE_ITEMS.find((item) => item.id === FORCE_MULTIPLIER_COPY.sourceExampleId) ??
  SHOWCASE_ITEMS[0];

/**
 * One real source frame branching toward three labeled output groups.
 * The source is an example from the showcase above, shown as a still —
 * the media fragment parks it on an early frame so a frame paints
 * without ever playing. Only the three connector strokes draw in.
 * Reduced motion renders everything finished.
 */
function ForceMultiplierVisual({ visible }) {
  return (
    <div
      className={`lp-force__visual${visible ? ' lp-force__visual--visible' : ''}`}
      role="img"
      aria-label="One source video branches into language, character, and variation outputs."
    >
      <div className="lp-force__canvas">
        <div className="lp-force__source">
          <div className="lp-force__source-screen">
            {/* Only fetched once the section is on screen. */}
            {visible ? (
              <video
                className="lp-force__source-video"
                src={`${SOURCE_EXAMPLE.videoUrl}#t=0.1`}
                preload="metadata"
                muted
                playsInline
                tabIndex={-1}
                aria-hidden="true"
              />
            ) : null}
            <span className="lp-force__source-play" aria-hidden="true">
              <Play size={20} weight="fill" />
            </span>
          </div>
        </div>

        <svg
          className="lp-force__connectors"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {CONNECTOR_PATHS.map((d, i) => (
            <path
              key={d}
              d={d}
              className={`lp-force__branch-path${i > 0 ? ` lp-force__branch-path--${i + 1}` : ''}`}
              fill="none"
              pathLength="1"
              stroke="currentColor"
              strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </svg>

        <div className="lp-force__groups">
          {FORCE_MULTIPLIER_COPY.branches.map((branch, i) => (
            <div
              className={`lp-force__group${i > 0 ? ` lp-force__group--${i + 1}` : ''}`}
              key={branch.id}
            >
              <div className="lp-force__group-head">
                <span className="lp-force__group-label">{branch.label}</span>
                <span className="lp-force__group-count">{branch.count}</span>
              </div>
              <div className="lp-force__chips" aria-hidden="true">
                {branch.items.map((item) => (
                  <span className="lp-force__chip" key={item.text}>
                    {item.flag ? (
                      <span className="lp-force__chip-flag">{item.flag}</span>
                    ) : null}
                    {item.text}
                  </span>
                ))}
                <span className="lp-force__chip lp-force__chip--more">{branch.more}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ForceMultiplier() {
  const reducedMotion = usePrefersReducedMotion();
  const [revealRef, revealed] = useLandingReveal();
  const visible = revealed || reducedMotion;

  return (
    <section className="lp-section lp-force" aria-labelledby="lp-force-heading">
      <div className="lp-container">
        <div
          ref={revealRef}
          className={`lp-reveal lp-force__grid${visible ? ' lp-reveal--visible' : ''}`}
        >
          <div className="lp-force__copy">
            <h2 id="lp-force-heading" className="lp-heading">
              {FORCE_MULTIPLIER_COPY.heading}
            </h2>
            <p className="lp-force__scale">{FORCE_MULTIPLIER_COPY.scale}</p>
          </div>
          <ForceMultiplierVisual visible={visible} />
        </div>
      </div>
    </section>
  );
}

export default ForceMultiplier;
